import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DatetimeService } from '~app/services/datetime.service';

@Component({
  selector: 'ciao-datetime-cell-renderer',
  standalone: true,
  template: `
    <p>{{ day }}</p>
    <p>{{ time }}</p>
  `,
  styles: `
    :host {
      display: inline-flex;
      width: 100%;
      align-items: center;
    }
    p {
      margin: 0;
    }
    /* Date flush-left; time stays in the right third (~2/3 across), where the
       previous even spacing had placed it. */
    p:first-child {
      flex: 2 1 0;
      text-align: left;
    }
    p:last-child {
      flex: 1 1 0;
      text-align: left;
    }
  `,
})
export class DateTimeCellRenderer
  implements ICellRendererAngularComp, OnInit, OnDestroy
{
  dateString: string;
  day: string;
  time: string;
  private lastParams: ICellRendererParams<any, any> | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(DatetimeService) private readonly dateTimeService: DatetimeService
  ) {}

  ngOnInit(): void {
    this.dateTimeService.timeZone$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.lastParams) {
          this.refresh(this.lastParams);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  agInit(params: ICellRendererParams<any, any>): void {
    this.refresh(params);
  }

  refresh(params: ICellRendererParams<any, any>) {
    this.lastParams = params;
    const rawVal = params.value;
    if (!rawVal || typeof rawVal !== 'string') {
      return false;
    }
    const normalized = this.dateTimeService.normalizeToUtcIfNoOffset(rawVal);
    const date = new Date(normalized);
    // Always use the current value from timeZone$ BehaviorSubject
    // This will be the user's profile timezone once loaded, or the default until then
    const tz =
      this.dateTimeService.timeZone$.value ||
      this.dateTimeService.browserDefaults.timeZone;

    this.day = Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      timeZone: tz,
    })
      .format(date)
      .replace(',', '');

    this.time = Intl.DateTimeFormat('en-US', {
      timeStyle: 'short',
      timeZone: tz,
    })
      .format(date)
      .replace(',', '');

    return true;
  }
}
